/* eslint-disable camelcase */
export enum UserActivityEvent {
  PAGE_VIEW = 'page_view',
  PURCHASE = 'purchase',
  SHOPPING_CART = 'shopping_cart',
  CLICK = 'click',
  PLAY_TRAILER = 'trailer',
  COMMENT_COURSE = 'comment',
  CREATE_COURSE_REVIEW = 'review',
  CREATE_PROJECT = 'upload_project',
  ENROLLMENT = 'enrollment',
  RECOMMENDATION_POSITION_CLICK = 'recommendation_position_click',
  RECOMMENDATION_POSITION_CHECKOUT = 'recommendation_position_checkout',
  RECOMMENDATION_VIEW = 'recommendation_view',
  FIRST_PLAY = 'play',
  DOWNLOAD_RESOURCE = 'download',
  READ_ARTICLE = 'read_article',
  CHECKOUT = 'checkout',
}

export interface GeneralTrackData {
  event_uuid: string;
  event_type: UserActivityEvent;
  event_date: string;
  segment: number;
  username: string;
  identifier?: string;
  app_platform: 'web';
  api_version: 2;
}

export interface UserData {
  segment: number;
  username: string;
}

export interface BaseParams {
  user?: UserData;
  nextUrl?: string;
}

export type PageViewType =
  | 'course'
  | 'tool'
  | 'ebook'
  | 'tutorial'
  | 'membership'
  | 'live'
  | 'custom_bundle'
  | 'bundle'
  | 'project'
  | 'career'
  | 'webinar'
  | 'podcast'
  | 'blog'
  | 'page';

export type PageIdentifierType = 'checkout';

export interface PageViewParams extends BaseParams {
  event: UserActivityEvent.PAGE_VIEW;
  data: {
    item_id?: number;
    item_type: PageViewType;
    path: string;
    origin_path: string;
    page_identifier?: PageIdentifierType;
  };
}

export interface PurchaseParams extends BaseParams {
  event: UserActivityEvent.PURCHASE;
  data: {
    product_id: number;
    product_name: string;
    item_type: 'custom_bundle' | 'course' | 'bundle' | 'membership';
    course_id?: number;
    course_name?: string;
    price: number;
    discount: number;
    order_id: number;
  };
}

export interface ShoppingCartParams extends BaseParams {
  event: UserActivityEvent.SHOPPING_CART;
  data: {
    product_id: number;
    product_name: string;
    item_type: 'course' | 'bundle';
    course_id: number;
    course_name: string;
    price: number;
    discount: number;
  };
}

export interface ClickParams extends BaseParams {
  event: UserActivityEvent.CLICK;
  data: {
    product_id: number;
    product_name: string;
    item_type: 'custom_bundle' | 'course' | 'bundle' | 'membership';
    item_path: string;
  };
}

export interface PlayTrailerParams extends BaseParams {
  event: UserActivityEvent.PLAY_TRAILER;
  data: {
    product_id: number;
    product_name: string;
    duration: number;
    progress: number;
    item_type: 'live' | 'course' | 'webinar';
  };
}

export interface CommentCourseParams extends BaseParams {
  event: UserActivityEvent.COMMENT_COURSE;
  data: {
    product_id: number;
    product_name: string;
    type: 'discussion' | 'reply';
  };
}

export interface ReviewParams extends BaseParams {
  event: UserActivityEvent.CREATE_COURSE_REVIEW;
  data: {
    product_id: number;
    product_name: string;
    score: number;
  };
}

export interface CreateProjectParams extends BaseParams {
  event: UserActivityEvent.CREATE_PROJECT;
  data: {
    product_id: number;
    product_name: string;
    item_type: 'project';
  };
}

export interface EnrollmentParams extends BaseParams {
  event: UserActivityEvent.ENROLLMENT;
  data: {
    product_id: number;
    product_name: string;
    enrollment_date: string;
    enrollment_type: number;
  };
}

export interface RecommendationPositionClickParams extends BaseParams {
  event: UserActivityEvent.RECOMMENDATION_POSITION_CLICK;
  data: {
    item_id?: number;
    item_type: 'course';
    recommendations: string;
    position_selected: number;
    origin: 'home_user' | 'course_detail' | 'shopping_cart' | 'onboarding';
    model_used:
      | 'UPS_MF_MI18N'
      | 'UPS_MF_MALL'
      | 'UPS_KNN_MSIM'
      | 'USER_RECOMM_SEGM_RT_ITEM'
      | 'IRECOMM_HYB_V1'
      | 'ANONYMOUS_RECOMM'
      | 'USER_RECOMM_SEGM';
  };
}

export interface RecommendationPositionCheckoutParams extends BaseParams {
  event: UserActivityEvent.RECOMMENDATION_POSITION_CHECKOUT;
  data: {
    item_id?: number;
    item_type: 'course';
    recommendations: string;
    position_selected: number;
    origin: 'home_user' | 'course_detail' | 'shopping_cart' | 'onboarding';
    model_used:
      | 'UPS_MF_MI18N'
      | 'UPS_MF_MALL'
      | 'UPS_KNN_MSIM'
      | 'USER_RECOMM_SEGM_RT_ITEM'
      | 'IRECOMM_HYB_V1'
      | 'ANONYMOUS_RECOMM'
      | 'USER_RECOMM_SEGM';
  };
}

export interface RecommendationViewParams extends BaseParams {
  event: UserActivityEvent.RECOMMENDATION_VIEW;
  data: {
    item_id?: number;
    item_type: 'course';
    recommendations: string;
    total_items: number;
    origin: 'home_user' | 'course_detail' | 'shopping_cart' | 'onboarding';
    model_used:
      | 'UPS_MF_MI18N'
      | 'UPS_MF_MALL'
      | 'UPS_KNN_MSIM'
      | 'USER_RECOMM_SEGM_RT_ITEM'
      | 'IRECOMM_HYB_V1'
      | 'ANONYMOUS_RECOMM'
      | 'USER_RECOMM_SEGM';
  };
}

export interface FirstPlayParams extends BaseParams {
  event: UserActivityEvent.FIRST_PLAY;
  data: {
    product_id: number;
    product_name: string;
    item_type: 'course' | 'trailer' | 'live' | 'tutorial';
  };
}

export interface DownloadResourceParams extends BaseParams {
  event: UserActivityEvent.DOWNLOAD_RESOURCE;
  data: {
    product_id: number;
    product_name: string;
    item_type: 'ebook' | 'tool';
  };
}

export interface ReadArticleParams extends BaseParams {
  event: UserActivityEvent.READ_ARTICLE;
  data: {
    product_id: number;
    product_name: string;
    item_type: 'article';
  };
}
export interface CheckoutParams extends BaseParams {
  event: UserActivityEvent.CHECKOUT;
  data: {
    product_id: number;
    item_type:
      | 'custom_bundle'
      | 'course'
      | 'bundle'
      | 'membership'
      | 'career'
      | 'shopping_cart';
    course_id: number;
  };
}
export type TrackUserActivity = (
  params:
    | PageViewParams
    | PurchaseParams
    | ShoppingCartParams
    | ClickParams
    | PlayTrailerParams
    | CommentCourseParams
    | ReviewParams
    | CreateProjectParams
    | EnrollmentParams
    | RecommendationPositionClickParams
    | RecommendationPositionCheckoutParams
    | RecommendationViewParams
    | FirstPlayParams
    | CheckoutParams,
) => Promise<{
  status: 'success' | 'error' | 'skipped';
  message?: string;
}>;
